
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useBadgeCount } from './useBadgeCount';
import { NavItem } from './types';
import TooltipSubmenu from './components/TooltipSubmenu';
import SubMenu from './components/SubMenu';

type CollapsibleNavItemProps = {
  item: NavItem;
  expanded: boolean;
  isMobile: boolean;
  expandedItems: Record<string, boolean>;
  toggleExpandItem: (title: string, event: React.MouseEvent) => void;
  handleNavigation: () => void;
};

const CollapsibleNavItem: React.FC<CollapsibleNavItemProps> = ({ 
  item, 
  expanded, 
  isMobile, 
  expandedItems,
  toggleExpandItem,
  handleNavigation 
}) => {
  const isExpanded = expandedItems[item.title];
  const badgeCount = useBadgeCount(item.title);
  const location = useLocation();
  
  // Check if any child route is active
  const isChildActive = React.useMemo(() => {
    if (!item.children) return false;
    
    return item.children.some(child => {
      const currentPath = location.pathname;
      const childPath = child.path;
      
      // Exact match
      if (currentPath === childPath) {
        return true;
      }
      
      // Special case for dashboard
      if ((childPath === '/dashboard' || childPath === '/') && 
          (currentPath === '/dashboard' || currentPath === '/')) {
        return true;
      }
      
      // For other paths, check if current path starts with child path
      if (childPath !== '/' && childPath !== '/dashboard') {
        // Handle path variations (with or without trailing slash)
        const normalizedChildPath = childPath.endsWith('/') ? childPath : `${childPath}/`;
        const normalizedCurrentPath = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
        
        return normalizedCurrentPath.startsWith(normalizedChildPath) || 
               currentPath.startsWith(childPath);
      }
      
      return false;
    });
  }, [item.children, location.pathname]);

  // Render submenu as tooltip when sidebar is collapsed
  if (!expanded && !isMobile && item.children) {
    return (
      <TooltipSubmenu
        item={item}
        badgeCount={badgeCount}
        isChildActive={isChildActive}
        handleNavigation={handleNavigation}
      />
    );
  }

  // Standard expanded view using Collapsible component
  return (
    <SubMenu
      item={item}
      expanded={expanded}
      isMobile={isMobile}
      isExpanded={isExpanded}
      isChildActive={isChildActive}
      badgeCount={badgeCount}
      toggleExpandItem={toggleExpandItem}
      handleNavigation={handleNavigation}
    />
  );
};

export default CollapsibleNavItem;
